import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Select, MenuItem, FormControl, TextField,
  Tabs, Tab
} from '@mui/material';
import { SketchPicker } from 'react-color';

const ALL_PLANETS = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];

const TYPE_DEFAULT_COLORS = {
  longitude_diff: '#2196F3',
  declination_diff: '#9C27B0',
  distance_percent_diff: '#FF9800',
  distance_percent: '#4CAF50',
  speed: '#F44336',
};

const isInvalidPair = (a, b) =>
  a === b || (a === 'Rahu' && b === 'Ketu') || (a === 'Ketu' && b === 'Rahu');

const pairExclude = (partner) => {
  const ex = [partner];
  if (partner === 'Rahu') ex.push('Ketu');
  if (partner === 'Ketu') ex.push('Rahu');
  return ex;
};
const LINE_THICKNESSES = ['1', '2', '3', '4', '5'];
const LINE_TYPES = ['Solid', 'Dashed', 'Dotted'];

const PlanetSelect = ({ value, onChange, planets, exclude }) => {
  const list = (planets || ALL_PLANETS).filter(p => !exclude || !exclude.includes(p));
  return (
    <FormControl size="small" sx={{ minWidth: 120 }}>
      <Select value={value} onChange={e => onChange(e.target.value)}>
        {list.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
      </Select>
    </FormControl>
  );
};
PlanetSelect.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  planets: PropTypes.array,
  exclude: PropTypes.array,
};

const RunBtn = ({ onClick }) => (
  <Button variant="contained" size="small" onClick={onClick}
    sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' }, minWidth: 64 }}>
    RUN
  </Button>
);
RunBtn.propTypes = { onClick: PropTypes.func.isRequired };

const RowLabel = ({ children }) => (
  <Typography sx={{ width: 280, textAlign: 'right', fontSize: 15, whiteSpace: 'nowrap' }}>
    {children}
  </Typography>
);
RowLabel.propTypes = { children: PropTypes.node.isRequired };

const PlanetCharacteristicsPlotDialog = ({ open, onClose, onRunSeasonality, onRunPivotAnalysis, onRunCharacteristics, barData }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [reportContent, setReportContent] = useState('');
  const [lonP1, setLonP1]   = useState('Sun');
  const [lonP2, setLonP2]   = useState('Moon');
  const [declP1, setDeclP1] = useState('Sun');
  const [declP2, setDeclP2] = useState('Moon');
  const [pctP1, setPctP1]   = useState('Sun');
  const [pctP2, setPctP2]   = useState('Moon');
  const [distP, setDistP]   = useState('Sun');
  const [speedP, setSpeedP] = useState('Sun');
  const [seasonalityDays, setSeasonalityDays] = useState('10');
  const [seasonalityMatchMode, setSeasonalityMatchMode] = useState('nearest');
  const [seasonalityDisplayType, setSeasonalityDisplayType] = useState('candlestick');

  // Pivot analysis parameters
  const [pivotVicinity, setPivotVicinity] = useState('2');
  const [pivotMinWindow, setPivotMinWindow] = useState('5');
  const [pivotMaxWindow, setPivotMaxWindow] = useState('50');
  const [factorThreshold, setFactorThreshold] = useState('40');
  const [futureThreshold, setFutureThreshold] = useState('30');
  const [projectionMonths, setProjectionMonths] = useState('12');
  const [pivotAsOfDate, setPivotAsOfDate] = useState('');

  // Date range for the as-of date picker
  const { minDate, maxDate, defaultDate } = useMemo(() => {
    if (!barData || !barData.dates || barData.dates.length === 0) {
      return { minDate: '', maxDate: '', defaultDate: '' };
    }
    const startDate = barData.dates[0].split('T')[0];
    // Min = start date + 10 years
    const startParts = startDate.split('-');
    const minY = Number(startParts[0]) + 10;
    const min = `${minY}-${startParts[1]}-${startParts[2]}`;
    const max = barData.dates[barData.dates.length - 1].split('T')[0];
    return { minDate: min, maxDate: max, defaultDate: max };
  }, [barData]);

  const [asOfDate, setAsOfDate] = useState('');

  const setPair = (setA, valA, setB, valB) => {
    setA(valA);
    if (isInvalidPair(valA, valB)) setB(ALL_PLANETS.find(p => !isInvalidPair(valA, p)) || ALL_PLANETS[0]);
  };

  const [color, setColor]               = useState('#E67C73');
  const [colorTouched, setColorTouched]   = useState(false);
  const [lineThickness, setLineThickness] = useState('2');
  const [lineType, setLineType]           = useState('Solid');
  const [panelChoice, setPanelChoice]     = useState('main');

  const getColor = (type) => colorTouched ? color : (TYPE_DEFAULT_COLORS[type] || '#E67C73');

  const handleRunPivot = (pivotType) => {
    if (typeof onRunPivotAnalysis === 'function') {
      onRunPivotAnalysis({
        pivotType,
        vicinity: Number(pivotVicinity) || 2,
        minPivot: Number(pivotMinWindow) || 5,
        maxPivot: Number(pivotMaxWindow) || 50,
        factorThreshold: (Number(factorThreshold) || 40) / 100,
        futureThreshold: (Number(futureThreshold) || 30) / 100,
        projectionMonths: Number(projectionMonths) || 12,
        asOfDate: pivotAsOfDate || undefined,
        setReportContent,
        switchToReport: () => setActiveTab(2),
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 20, pb: 0 }}>
        Characteristics Plot Dialog
      </DialogTitle>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} centered sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Characteristics" />
        <Tab label="Projections" />
        <Tab label="Report" />
      </Tabs>

      <DialogContent sx={{ pt: 1, minHeight: 480 }}>

        {/* ══════ TAB 1: Characteristics ══════ */}
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-start' }}>

            {/* LEFT: 5 study rows */}
            <Box sx={{ flex: '0 0 auto', pt: 1 }}>

              {/* Panel choice radio */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>Plot on:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <input type="radio" checked={panelChoice === 'main'} onChange={() => setPanelChoice('main')} />
                  <Typography sx={{ fontSize: 13 }}>Main Panel</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <input type="radio" checked={panelChoice === 'bottom'} onChange={() => setPanelChoice('bottom')} />
                  <Typography sx={{ fontSize: 13 }}>Volume Panel</Typography>
                </Box>
              </Box>

              {/* Row 1: Longitude difference */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <RowLabel>Longitude difference of Planet</RowLabel>
                <PlanetSelect value={lonP1} onChange={v => setPair(setLonP1, v, setLonP2, lonP2)} exclude={pairExclude(lonP2)} />
                <Typography sx={{ fontSize: 15, whiteSpace: 'nowrap' }}>And Planet</Typography>
                <PlanetSelect value={lonP2} onChange={v => setPair(setLonP2, v, setLonP1, lonP1)} exclude={pairExclude(lonP1)} />
                <RunBtn onClick={() => {
                  if (typeof onRunCharacteristics === 'function') {
                    onRunCharacteristics({
                      characteristicType: 'longitude_diff',
                      planet1: lonP1,
                      planet2: lonP2,
                      color: getColor('longitude_diff'),
                      lineThickness,
                      lineType,
                      panel: panelChoice,
                    });
                  }
                }} />
              </Box>

              {/* Row 2: Declination difference */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <RowLabel>Declination difference of Planet</RowLabel>
                <PlanetSelect value={declP1} onChange={v => setPair(setDeclP1, v, setDeclP2, declP2)} exclude={pairExclude(declP2)} />
                <Typography sx={{ fontSize: 15, whiteSpace: 'nowrap' }}>And Planet</Typography>
                <PlanetSelect value={declP2} onChange={v => setPair(setDeclP2, v, setDeclP1, declP1)} exclude={pairExclude(declP1)} />
                <RunBtn onClick={() => {
                  if (typeof onRunCharacteristics === 'function') {
                    onRunCharacteristics({
                      characteristicType: 'declination_diff',
                      planet1: declP1,
                      planet2: declP2,
                      color: getColor('declination_diff'),
                      lineThickness,
                      lineType,
                      panel: panelChoice,
                    });
                  }
                }} />
              </Box>

              {/* Row 3: Percentage distance difference */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <RowLabel>Percentage distance difference of Planet</RowLabel>
                <PlanetSelect value={pctP1} onChange={v => setPair(setPctP1, v, setPctP2, pctP2)} exclude={pairExclude(pctP2)} />
                <Typography sx={{ fontSize: 15, whiteSpace: 'nowrap' }}>And Planet</Typography>
                <PlanetSelect value={pctP2} onChange={v => setPair(setPctP2, v, setPctP1, pctP1)} exclude={pairExclude(pctP1)} />
                <RunBtn onClick={() => {
                  if (typeof onRunCharacteristics === 'function') {
                    onRunCharacteristics({
                      characteristicType: 'distance_percent_diff',
                      planet1: pctP1,
                      planet2: pctP2,
                      color: getColor('distance_percent_diff'),
                      lineThickness,
                      lineType,
                      panel: panelChoice,
                    });
                  }
                }} />
              </Box>

              {/* Row 4: Distance */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <RowLabel>Distance of Planet</RowLabel>
                <PlanetSelect value={distP} onChange={setDistP} />
                <RunBtn onClick={() => {
                  if (typeof onRunCharacteristics === 'function') {
                    onRunCharacteristics({
                      characteristicType: 'distance_percent',
                      planet1: distP,
                      color: getColor('distance_percent'),
                      lineThickness,
                      lineType,
                      panel: panelChoice,
                    });
                  }
                }} />
              </Box>

              {/* Row 5: Speed */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <RowLabel>Speed Of Planet</RowLabel>
                <PlanetSelect value={speedP} onChange={setSpeedP} />
                <RunBtn onClick={() => {
                  if (typeof onRunCharacteristics === 'function') {
                    onRunCharacteristics({
                      characteristicType: 'speed',
                      planet1: speedP,
                      color: getColor('speed'),
                      lineThickness,
                      lineType,
                      panel: panelChoice,
                    });
                  }
                }} />
              </Box>
            </Box>

            {/* RIGHT: color picker + line options */}
            <Box sx={{ flex: '0 0 auto', border: '1px solid #ccc', borderRadius: 1, p: 2 }}>
              <Typography fontWeight="bold" fontSize={15} sx={{ mb: 1.5 }}>Select highlight color</Typography>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                <SketchPicker color={color} onChangeComplete={c => { setColor(c.hex); setColorTouched(true); }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 0.5 }}>
                  <Box>
                    <Typography fontWeight="bold" fontSize={14} sx={{ mb: 0.5 }}>Line Thickness</Typography>
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <Select value={lineThickness} onChange={e => setLineThickness(e.target.value)}>
                        {LINE_THICKNESSES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography fontWeight="bold" fontSize={14} sx={{ mb: 0.5 }}>Line Type</Typography>
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <Select value={lineType} onChange={e => setLineType(e.target.value)}>
                        {LINE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>
            </Box>

          </Box>
        )}

        {/* ══════ TAB 2: Projections & Pivot Analysis ══════ */}
        {activeTab === 1 && (
          <Box sx={{ pt: 1 }}>

            {/* Seasonality Projection */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <RowLabel>Use Totality Projection System For</RowLabel>
              <TextField
                size="small"
                value={seasonalityDays}
                onChange={e => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  if (val === '') { setSeasonalityDays(''); return; }
                  const num = Math.min(22, Math.max(5, Number(val)));
                  setSeasonalityDays(String(num));
                }}
                onBlur={() => {
                  if (seasonalityDays === '' || Number(seasonalityDays) < 5) setSeasonalityDays('5');
                }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 5, max: 22 }}
                sx={{ width: 70 }}
              />
              <Typography sx={{ fontSize: 15, whiteSpace: 'nowrap' }}>trading days</Typography>
              <Box sx={{ flex: 1 }} />
              <RunBtn onClick={() => {
                if (typeof onRunSeasonality === 'function') {
                  onRunSeasonality({ tradingDays: Number(seasonalityDays) || 10, matchMode: seasonalityMatchMode, displayType: seasonalityDisplayType, asOfDate: asOfDate || defaultDate });
                }
              }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: '292px', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <input type="radio" checked={seasonalityMatchMode === 'nearest'} onChange={() => setSeasonalityMatchMode('nearest')} />
                <Typography sx={{ fontSize: 13 }}>Nearest trading day</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <input type="radio" checked={seasonalityMatchMode === 'exact'} onChange={() => setSeasonalityMatchMode('exact')} />
                <Typography sx={{ fontSize: 13 }}>Exact date only</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: '292px', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <input type="radio" checked={seasonalityDisplayType === 'line'} onChange={() => setSeasonalityDisplayType('line')} />
                <Typography sx={{ fontSize: 13 }}>Line</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <input type="radio" checked={seasonalityDisplayType === 'candlestick'} onChange={() => setSeasonalityDisplayType('candlestick')} />
                <Typography sx={{ fontSize: 13 }}>Candlestick</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: '292px', mb: 2 }}>
              <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>As-of date:</Typography>
              <TextField
                type="date"
                size="small"
                value={asOfDate || defaultDate}
                onChange={e => setAsOfDate(e.target.value)}
                inputProps={{ min: minDate, max: maxDate }}
                sx={{ width: 160 }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setAsOfDate(defaultDate)}
                sx={{ textTransform: 'none', fontSize: 12, minWidth: 50 }}
              >Reset</Button>
            </Box>

            {/* Pivot Analysis parameters */}
            <Box sx={{ mt: 4, border: '1px solid #ccc', borderRadius: 1, p: 2 }}>
              <Typography fontWeight="bold" fontSize={15} sx={{ mb: 1.5 }}>Pivot Analysis Parameters</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>Min Pivot:</Typography>
                  <TextField size="small" value={pivotMinWindow}
                    onChange={e => setPivotMinWindow(e.target.value.replace(/[^0-9]/g, ''))}
                    inputProps={{ min: 3, max: 50 }} sx={{ width: 60 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>Max Pivot:</Typography>
                  <TextField size="small" value={pivotMaxWindow}
                    onChange={e => setPivotMaxWindow(e.target.value.replace(/[^0-9]/g, ''))}
                    inputProps={{ min: 5, max: 100 }} sx={{ width: 60 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>Vicinity (±bars):</Typography>
                  <TextField size="small" value={pivotVicinity}
                    onChange={e => setPivotVicinity(e.target.value.replace(/[^0-9]/g, ''))}
                    inputProps={{ min: 0, max: 10 }} sx={{ width: 50 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>Factor Threshold (%):</Typography>
                  <TextField size="small" value={factorThreshold}
                    onChange={e => setFactorThreshold(e.target.value.replace(/[^0-9]/g, ''))}
                    inputProps={{ min: 10, max: 100 }} sx={{ width: 55 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>Future Threshold (%):</Typography>
                  <TextField size="small" value={futureThreshold}
                    onChange={e => setFutureThreshold(e.target.value.replace(/[^0-9]/g, ''))}
                    inputProps={{ min: 10, max: 100 }} sx={{ width: 55 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>Projection (months):</Typography>
                  <TextField size="small" value={projectionMonths}
                    onChange={e => setProjectionMonths(e.target.value.replace(/[^0-9]/g, ''))}
                    inputProps={{ min: 1, max: 60 }} sx={{ width: 55 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>As-of Date:</Typography>
                  <TextField size="small" type="date" value={pivotAsOfDate}
                    onChange={e => setPivotAsOfDate(e.target.value)}
                    inputProps={{ min: minDate, max: maxDate }}
                    sx={{ width: 155 }} />
                  <Button size="small" variant="outlined"
                    onClick={() => setPivotAsOfDate('')}
                    sx={{ textTransform: 'none', fontSize: 11, minWidth: 45, py: 0.3 }}
                  >Clear</Button>
                </Box>
              </Box>

              {/* Pivot Analysis RUN buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography sx={{ fontSize: 15, flex: 1 }}>Conduct High Pivot Analysis and generate report</Typography>
                  <RunBtn onClick={() => handleRunPivot('HP')} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography sx={{ fontSize: 15, flex: 1 }}>Conduct Low Pivot Analysis and generate report</Typography>
                  <RunBtn onClick={() => handleRunPivot('LP')} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography sx={{ fontSize: 15, flex: 1 }}>Run both types of analysis and generate report</Typography>
                  <RunBtn onClick={() => handleRunPivot('BOTH')} />
                </Box>
              </Box>
            </Box>

          </Box>
        )}

        {/* ══════ TAB 3: Report ══════ */}
        {activeTab === 2 && (
          <Box
            sx={{
              border: '2px solid #333',
              borderRadius: 1,
              p: 2,
              minHeight: 400,
              maxHeight: 500,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: 14,
              whiteSpace: 'pre-wrap',
              backgroundColor: '#fff',
            }}
          >
            {reportContent || <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>No report generated yet. Run a pivot analysis from the Projections tab.</Typography>}
          </Box>
        )}

      </DialogContent>

      <DialogActions sx={{ p: '12px 24px' }}>
        <Button onClick={onClose} variant="contained" color="inherit">CLOSE</Button>
      </DialogActions>
    </Dialog>
  );
};

PlanetCharacteristicsPlotDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRunSeasonality: PropTypes.func,
  onRunPivotAnalysis: PropTypes.func,
  onRunCharacteristics: PropTypes.func,
  barData: PropTypes.object,
};

export default PlanetCharacteristicsPlotDialog;
